import React, { ReactNode, createContext, useContext, useState, useRef, useEffect } from 'react';
import useMin from '../hooks/useMin';
import useMax from '../hooks/useMax';
import { SELECTED_FEATURE_PROPERTY } from '../constants/mapConstants';
import IColorInfo from '../interfaces/IColorInfo';
import { CIRCLE_COLORS } from '../constants/circleColors';

interface IVariables {
	map?: any;
	mapRef?: any;
	rawData?: any;
	isClustered?: boolean;
	selectedFeatureProperty?: any;
	min?: number;
	max?: number;
	hoveredFeature?: any;
	hoveredCluster?: any;
	colorInfo?: IColorInfo[];
	clusterColorInfo?: IColorInfo[];
	data?: any;
}

interface IFunctions {
	setMap?: any;
	setRawData?: any;
	setIsClustered?: any;
	setSelectedFeatureProperty?: any;
	setMin?: any;
	setMax?: any;
	setHoveredFeature?: any;
	setHoveredCluster?: any;
	setColorInfo?: any;
	setClusterColorInfo?: any;
	setData?: any;
}

const MapContext = createContext<[IVariables, IFunctions]>([{}, {}]);

interface IProvider {
	children: ReactNode;
}
export const MapProvider = (props: IProvider) => {
	const [map, setMap] = useState();
	const mapRef = useRef();
	const [data, setData] = useState<any>();
	const [rawData, setRawData] = useState<any>();
	const [isClustered, setIsClustered] = useState<boolean>(true);
	const [selectedFeatureProperty, setSelectedFeatureProperty] = useState<string>('Demo Property');
	const [{ min }, { setMin }] = useMin({ data: rawData, property: SELECTED_FEATURE_PROPERTY });
	const [{ max }, { setMax }] = useMax({ data: rawData, property: SELECTED_FEATURE_PROPERTY });
	const [hoveredFeature, setHoveredFeature] = useState<any>(undefined);
	const [hoveredCluster, setHoveredCluster] = useState<any>(undefined);
	const [colorInfo, setColorInfo] = useState<IColorInfo[]>([]);
	const [clusterColorInfo, setClusterColorInfo] = useState<IColorInfo[]>([]);

	useEffect(() => {
		let difference = max - min;
		if (difference < 6 || isNaN(min) || isNaN(max)) difference = 6;
		const step = Math.ceil(difference / 6);
		let rtn: IColorInfo[] = [];
		for (let i = 0; i < 6; i++) {
			const value = step * i + min;
			const maxValue = value + step - 1;
			rtn.push({ maxValue: maxValue, minValue: value, color: CIRCLE_COLORS[i] });
		}
		rtn = rtn.sort((a: IColorInfo, b: IColorInfo) => {
			if (a.maxValue < b.maxValue) return 1;
			else if (a.maxValue > b.maxValue) return -1;
			else return 0;
		});
		setColorInfo(rtn);
	}, [min, max]);

	useEffect(() => {
		let rtn: IColorInfo[] = [
			{ minValue: min, maxValue: max, color: CIRCLE_COLORS[0] },
			{ minValue: max + 1, maxValue: max * 2, color: CIRCLE_COLORS[1] },
			{ minValue: max * 2 + 1, maxValue: max * 4, color: CIRCLE_COLORS[2] },
			{ minValue: max * 4 + 1, maxValue: max * 8, color: CIRCLE_COLORS[3] },
			{ minValue: max * 8 + 1, maxValue: max * 16, color: CIRCLE_COLORS[4] },
			{ minValue: max * 16 + 1, maxValue: Infinity, color: CIRCLE_COLORS[5] },
		];
		setClusterColorInfo(rtn);
	}, [min, max]);

	const value: [IVariables, IFunctions] = [
		{
			map,
			mapRef,
			rawData,
			isClustered,
			selectedFeatureProperty,
			min,
			max,
			hoveredFeature,
			hoveredCluster,
			colorInfo,
			clusterColorInfo,
			data,
		},
		{
			setMap,
			setRawData,
			setIsClustered,
			setSelectedFeatureProperty,
			setMin,
			setMax,
			setHoveredFeature,
			setHoveredCluster,
			setColorInfo,
			setClusterColorInfo,
			setData,
		},
	];

	return <MapContext.Provider value={value} {...props} />;
};

export const useMapContext = () => {
	const context = useContext(MapContext);
	if (!context) {
		throw new Error('useMapContext must be used within a MapProvider');
	}
	return context;
};
